// Topbar — brand mark + section tabs (desktop) + current section label
// (mobile) + search/bell/avatar. Hands the avatar click to the parent so
// the user menu can be wired alongside the tweaks/admin overlays.

import type { Me } from "../lib/use-me";
import { Icon, type IconName } from "../icons";
import { OfflineIndicator } from "./offline-indicator";
import { NotificationsBell } from "./notifications-bell";

export interface Tab {
  id: string;
  label: string;
  icon: IconName;
}

interface TopbarProps {
  tabs: Tab[];
  tab: string;
  setTab: (id: string) => void;
  me: Me | null;
  onAvatar: () => void;
  onSearch: () => void;
  /** Bell-row click handlers — parent routes by notification kind. */
  onOpenPantry: () => void;
  onOpenChores: () => void;
  onOpenFamily: () => void;
  onOpenCalendar: () => void;
  /** Active kiosk profile if the SPA is running in kiosk mode (e.g.
   *  "kitchen" for the counter-mounted Surface device). When set, the
   *  top-right chrome is stripped down and a "Listening" pill is shown
   *  in place of the user controls so the kiosk surface stays voice-
   *  first. Null on normal loads. */
  kioskProfile?: string | null;
}

export function Topbar({ tabs, tab, setTab, me, onAvatar, onSearch, onOpenPantry, onOpenChores, onOpenFamily, onOpenCalendar, kioskProfile }: TopbarProps) {
  const current = tabs.find((t) => t.id === tab) || tabs[0];
  return (
    <header className="topbar">
      <div className="brand">
        <div className="brand-dot" aria-hidden />
        <span>Hub</span>
        <span className="brand-italic">/&nbsp;your day</span>
      </div>

      <nav className="tabs desk-only">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-active={tab === t.id}
            className="tab"
            onClick={() => setTab(t.id)}
          >
            <Icon name={t.icon} />
            <span>{t.label}</span>
            {tab === t.id && <span className="dot" />}
          </button>
        ))}
      </nav>

      {/* Mobile: show current section name only */}
      <div
        className="mobile-only"
        style={{ flex: 1, alignItems: "center", gap: 8, color: "var(--text-mute)" }}
      >
        <Icon name={current.icon} size={15} />
        <span style={{ fontSize: 13.5, fontWeight: 500, color: "var(--text)" }}>
          {current.label}
        </span>
      </div>

      <div className="topbar-right">
        <OfflineIndicator />
        {kioskProfile === "kitchen" ? (
          // Kitchen kiosk: strip the user-controls entirely (Settings /
          // Search palette / account menu exist for normal sessions on
          // other clients). No "Listening" pill either — the device is
          // always listening, the indicator just adds noise.
          null
        ) : (
          <>
            <button className="icon-btn" title="Search (⌘K)" onClick={onSearch}><Icon name="search" /></button>
            <NotificationsBell
              onOpenPantry={onOpenPantry}
              onOpenChores={onOpenChores}
              onOpenFamily={onOpenFamily}
              onOpenCalendar={onOpenCalendar}
            />
            <button
              className="avatar"
              title={me ? `${me.username} · ${me.role}` : "Account"}
              onClick={onAvatar}
              style={{ border: 0, cursor: "default", padding: 0 }}
            >
              {me?.initial ?? "?"}
            </button>
          </>
        )}
      </div>
    </header>
  );
}
